import { useRef } from 'react';
import { Num, T } from 'gt-react';
import { ImagesIcon } from '@phosphor-icons/react';
import { useFileDrop } from '@/hooks/useFileDrop';
import {
  FEEDBACK_IMAGE_ACCEPT,
  MAX_FEEDBACK_IMAGES,
} from './feedbackAttachmentRules';

/**
 * Compact drop target + hidden file picker for report images. Validation
 * happens in the caller (`addFiles`), which also receives pasted files.
 */
export function FeedbackDropzone({
  onFilesSelected,
  disabled,
  remaining,
}: {
  onFilesSelected: (files: File[]) => void;
  disabled?: boolean;
  remaining: number;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { isDragActive, dropTargetProps } = useFileDrop({
    onDropFiles: onFilesSelected,
    disabled: disabled || remaining === 0,
  });
  const full = remaining === 0;

  return (
    <div
      {...dropTargetProps}
      className={`rounded-md border border-dashed px-3 py-2 text-xs transition-colors ${
        isDragActive
          ? 'border-primary bg-primary/5'
          : 'border-muted-foreground/30 bg-muted/30'
      }`}
    >
      <button
        type="button"
        disabled={disabled || full}
        onClick={() => inputRef.current?.click()}
        className="flex w-full items-center gap-2 text-left text-muted-foreground disabled:cursor-not-allowed disabled:opacity-60"
      >
        <ImagesIcon className="h-4 w-4 shrink-0" />
        {full ? (
          <T context="dropzone text when the maximum number of images is attached">
            Maximum of <Num>{MAX_FEEDBACK_IMAGES}</Num> images attached
          </T>
        ) : (
          <T context="dropzone text inviting the user to add screenshots to a feedback">
            Drop images here, click to browse, or paste a screenshot
          </T>
        )}
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={FEEDBACK_IMAGE_ACCEPT}
        className="hidden"
        onChange={(e) => {
          const picked = Array.from(e.target.files ?? []);
          e.target.value = '';
          if (picked.length > 0) onFilesSelected(picked);
        }}
      />
    </div>
  );
}
